// client/src/pages/admin/Fuel.tsx
import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Tag,
  Badge,
  Row,
  Col,
  message,
  Dropdown,
  Statistic,
  Progress,
  Alert
} from 'antd';
import {
  ThunderboltOutlined,
  PlusOutlined,
  CarOutlined,
  DollarOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  DownloadOutlined,
  BarChartOutlined,
  LineChartOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { fuelService, type FuelRecord, type CreateFuelRecordData } from '../../services/fuelService';
import { vehicleService } from '../../services/vehicleService';
import { driverService } from '../../services/driverService';

const { Option } = Select;
const { RangePicker } = DatePicker;

const AdminFuel: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FuelRecord | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch fuel records
  const { data: fuelData, isLoading } = useQuery({
    queryKey: ['fuelRecords', selectedVehicleId, dateRange],
    queryFn: () => {
      const filters: any = { limit: 100 };
      if (selectedVehicleId) filters.vehicle_id = selectedVehicleId;
      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.start_date = dateRange[0].toISOString();
        filters.end_date = dateRange[1].toISOString();
      }
      return fuelService.getFuelRecords(filters);
    },
  });

  // Fetch fuel statistics
  const { data: stats } = useQuery({
    queryKey: ['fuelStatistics'],
    queryFn: () => fuelService.getFuelStatistics(),
  });

  // Fetch vehicles for select dropdown
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => vehicleService.getVehicles({ limit: 100 }),
  });

  // Fetch drivers for select dropdown
  const { data: driversData } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => driverService.getDrivers({ limit: 100 }),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateFuelRecordData) => fuelService.createFuelRecord(data),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message);
      queryClient.invalidateQueries({ queryKey: ['fuelRecords'] });
      queryClient.invalidateQueries({ queryKey: ['fuelStatistics'] });
      setModalVisible(false);
      form.resetFields();
      setEditingRecord(null);
    },
    onError: () => {
      message.error('Erreur lors de la création de l\'enregistrement');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      fuelService.updateFuelRecord(id, data),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message);
      queryClient.invalidateQueries({ queryKey: ['fuelRecords'] });
      queryClient.invalidateQueries({ queryKey: ['fuelStatistics'] });
      setModalVisible(false);
      form.resetFields();
      setEditingRecord(null);
    },
    onError: () => {
      message.error('Erreur lors de la modification de l\'enregistrement');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => fuelService.deleteFuelRecord(id),
    onSuccess: (response: { message?: string }) => {
      message.success(response.message);
      queryClient.invalidateQueries({ queryKey: ['fuelRecords'] });
      queryClient.invalidateQueries({ queryKey: ['fuelStatistics'] });
    },
    onError: () => {
      message.error('Erreur lors de la suppression de l\'enregistrement');
    },
  });

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      fuel_type: 'diesel',
      recorded_at: dayjs(),
      price_per_unit: 1.45,
      is_full_tank: true,
    });
    setModalVisible(true);
  };

  const handleEdit = (record: FuelRecord) => {
    setEditingRecord(record);
    form.setFieldsValue({
      vehicle_id: record.vehicle_id,
      driver_id: record.driver_id,
      fuel_type: record.fuel_type,
      quantity: record.quantity,
      price_per_unit: record.price_per_unit,
      mileage: record.mileage,
      location: record.location,
      station_name: record.station_name,
      receipt_number: record.receipt_number,
      notes: record.notes,
      is_full_tank: record.is_full_tank,
      recorded_at: dayjs(record.recorded_at),
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: 'Supprimer l\'enregistrement',
      content: 'Êtes-vous sûr de vouloir supprimer cet enregistrement ?',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleSubmit = async (values: any) => {
    const recordData = {
      vehicle_id: values.vehicle_id,
      driver_id: values.driver_id,
      fuel_type: values.fuel_type,
      quantity: values.quantity,
      price_per_unit: values.price_per_unit,
      mileage: values.mileage,
      location: values.location,
      station_name: values.station_name,
      receipt_number: values.receipt_number,
      notes: values.notes,
      is_full_tank: values.is_full_tank,
      recorded_at: values.recorded_at.toISOString(),
    };

    if (editingRecord) {
      updateMutation.mutate({ id: editingRecord.id, data: recordData });
    } else {
      createMutation.mutate(recordData as CreateFuelRecordData);
    }
  };

  const getFuelTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      diesel: 'orange',
      essence: 'red',
      sp95: 'red',
      sp98: 'volcano',
      electrique: 'green',
      hybride: 'blue',
      gpl: 'purple',
    };
    return colors[type] || 'default';
  };

  const getFuelTypeText = (type: string) => {
    const texts: Record<string, string> = {
      diesel: 'Diesel',
      essence: 'Essence',
      sp95: 'SP95',
      sp98: 'SP98',
      electrique: 'Électrique',
      hybride: 'Hybride',
      gpl: 'GPL',
    };
    return texts[type] || type;
  };

  const getFuelIcon = (type: string) => {
    if (type === 'electrique') {
      return '⚡';
    }
    return '⛽';
  };

  const columns: ColumnsType<FuelRecord> = [
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (_, record) => (
        <Space>
          <CarOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.license_plate || 'N/A'}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.brand} {record.model}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Conducteur',
      key: 'driver',
      render: (_, record) =>
        record.driver_first_name ? (
          <div>
            {record.driver_first_name} {record.driver_last_name}
          </div>
        ) : (
          <span style={{ color: '#999' }}>Non renseigné</span>
        ),
    },
    {
      title: 'Date',
      dataIndex: 'recorded_at',
      key: 'recorded_at',
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format('DD/MM/YYYY')}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{dayjs(date).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Carburant',
      key: 'fuel',
      render: (_, record) => (
        <Space>
          <span style={{ fontSize: '16px' }}>{getFuelIcon(record.fuel_type)}</span>
          <Tag color={getFuelTypeColor(record.fuel_type)}>
            {getFuelTypeText(record.fuel_type)}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number, record) => (
        <div>
          {quantity.toFixed(1)} {record.fuel_type === 'electrique' ? 'kWh' : 'L'}
        </div>
      ),
    },
    {
      title: 'Prix unitaire',
      dataIndex: 'price_per_unit',
      key: 'price_per_unit',
      render: (price: number, record) => (
        <div>
          {price.toFixed(2)}€/{record.fuel_type === 'electrique' ? 'kWh' : 'L'}
        </div>
      ),
    },
    {
      title: 'Coût total',
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (cost: number) => (
        <Space>
          <DollarOutlined />
          <strong>{cost.toFixed(2)}€</strong>
        </Space>
      ),
    },
    {
      title: 'Consommation',
      dataIndex: 'consumption',
      key: 'consumption',
      render: (consumption: number | undefined, record) =>
        consumption ? (
          <div>
            {consumption.toFixed(1)} {record.fuel_type === 'electrique' ? 'kWh' : 'L'}/100km
          </div>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        ),
    },
    {
      title: 'Kilométrage',
      dataIndex: 'mileage',
      key: 'mileage',
      render: (mileage: number) => (
        <div>
          {mileage.toLocaleString()} km
          {mileage > 0 && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {mileage > 100000 ? '⚠️ Kilométrage élevé' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Station',
      key: 'station_info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.station_name || 'Non renseigné'}</div>
          {record.location && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.location}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Modifier',
            onClick: () => handleEdit(record),
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Supprimer',
            danger: true,
            onClick: () => handleDelete(record.id),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const records = fuelData?.records || [];
  const vehicles = (vehiclesData as any)?.data?.vehicles || [];
  const drivers = (driversData as any)?.data?.drivers || [];

  // Calculate statistics from filtered records
  const totalCost = records.reduce((sum: number, record: any) => sum + record.total_cost, 0);
  const totalQuantity = records.reduce((sum: number, record: any) => sum + record.quantity, 0);
  const recordsWithConsumption = records.filter((r: any) => r.consumption);
  const averageConsumption =
    recordsWithConsumption.length > 0
      ? recordsWithConsumption.reduce((sum: number, r: any) => sum + (r.consumption || 0), 0) /
        recordsWithConsumption.length
      : 0;
  const averagePrice = totalQuantity > 0 ? totalCost / totalQuantity : 0;

  // Get alerts from statistics
  const alerts = stats?.alerts || [];

  return (
    <div style={{ padding: '24px' }}>
      {/* Alertes */}
      {alerts.length > 0 && (
        <Alert
          message={`${alerts.length} alerte(s) de consommation anormale`}
          description={
            <div>
              {alerts.slice(0, 3).map((alert: any) => (
                <div key={alert.vehicle_id} style={{ marginTop: '8px' }}>
                  <strong>{alert.license_plate}</strong>: {alert.alert_message} (Consommation
                  actuelle: {alert.current_consumption.toFixed(1)}L/100km, Moyenne:{' '}
                  {alert.avg_consumption.toFixed(1)}L/100km)
                </div>
              ))}
              {alerts.length > 3 && <div style={{ marginTop: '8px' }}>Et {alerts.length - 3} autre(s)...</div>}
            </div>
          }
          type="warning"
          style={{ marginBottom: '24px' }}
          showIcon
          icon={<WarningOutlined />}
        />
      )}

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût total"
              value={totalCost}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="€"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Volume total"
              value={totalQuantity}
              precision={1}
              prefix={<ThunderboltOutlined />}
              suffix="L"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Prix moyen"
              value={averagePrice}
              precision={2}
              suffix="€/L"
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Consommation moyenne"
              value={averageConsumption}
              precision={1}
              suffix="L/100km"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Graphiques de consommation */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <LineChartOutlined />
                Évolution des coûts
              </Space>
            }
          >
            <div
              style={{
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
              }}
            >
              Graphique d'évolution des coûts carburant
              <br />
              <small>(Intégration Chart.js à venir)</small>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BarChartOutlined />
                Consommation par véhicule
              </Space>
            }
          >
            <div style={{ height: '200px' }}>
              {stats?.by_vehicle.slice(0, 5).map((item: any, index: number) => (
                <div key={item.vehicle_id} style={{ marginBottom: '12px' }}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                  >
                    <span>{item.license_plate}</span>
                    <span>{item.avg_consumption ? item.avg_consumption.toFixed(1) : '-'}L/100km</span>
                  </div>
                  <Progress
                    percent={item.avg_consumption ? (item.avg_consumption / 25) * 100 : 0}
                    showInfo={false}
                    strokeColor={['#52c41a', '#fa8c16', '#ff4d4f', '#1890ff', '#722ed1'][index % 5]}
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tableau principal */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            Gestion du carburant
            <Badge count={records.length} style={{ backgroundColor: '#52c41a' }} />
          </Space>
        }
        extra={
          <Space wrap>
            <Select
              placeholder="Tous les véhicules"
              value={selectedVehicleId}
              onChange={setSelectedVehicleId}
              allowClear
              style={{ width: 200 }}
            >
              {vehicles.map((vehicle: any) => (
                <Option key={vehicle.id} value={vehicle.id}>
                  {vehicle.license_plate} - {vehicle.brand} {vehicle.model}
                </Option>
              ))}
            </Select>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              style={{ marginRight: '8px' }}
            />
            <Button
              icon={<DownloadOutlined />}
              onClick={() => message.info('Export en cours de développement')}
            >
              Exporter
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Nouveau plein
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: records.length,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} enregistrements`,
          }}
        />
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={editingRecord ? "Modifier l'enregistrement" : 'Nouveau plein'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingRecord(null);
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="vehicle_id"
                label="Véhicule"
                rules={[{ required: true, message: 'Le véhicule est requis' }]}
              >
                <Select placeholder="Sélectionner un véhicule" showSearch optionFilterProp="children">
                  {vehicles.map((vehicle: any) => (
                    <Option key={vehicle.id} value={vehicle.id}>
                      {vehicle.license_plate} - {vehicle.brand} {vehicle.model}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="driver_id" label="Conducteur (optionnel)">
                <Select placeholder="Sélectionner un conducteur" showSearch optionFilterProp="children" allowClear>
                  {drivers.map((driver: any) => (
                    <Option key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="fuel_type"
                label="Type de carburant"
                rules={[{ required: true, message: 'Le type est requis' }]}
              >
                <Select placeholder="Type de carburant">
                  <Option value="diesel">Diesel</Option>
                  <Option value="essence">Essence</Option>
                  <Option value="sp95">SP95</Option>
                  <Option value="sp98">SP98</Option>
                  <Option value="gpl">GPL</Option>
                  <Option value="electrique">Électrique</Option>
                  <Option value="hybride">Hybride</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="quantity"
                label="Quantité"
                rules={[{ required: true, message: 'La quantité est requise' }]}
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  style={{ width: '100%' }}
                  placeholder="65.5"
                  addonAfter="L/kWh"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="price_per_unit"
                label="Prix unitaire (€)"
                rules={[{ required: true, message: 'Le prix est requis' }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="1.45" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="recorded_at"
                label="Date et heure"
                rules={[{ required: true, message: 'La date est requise' }]}
              >
                <DatePicker
                  showTime
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: '100%' }}
                  placeholder="Sélectionner la date"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="mileage"
                label="Kilométrage"
                rules={[{ required: true, message: 'Le kilométrage est requis' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="95420"
                  addonAfter="km"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="station_name" label="Station service">
                <Input placeholder="Total Access" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="location" label="Lieu">
                <Input placeholder="Paris 11ème" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="receipt_number" label="Numéro de reçu">
                <Input placeholder="REC001234" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="is_full_tank" label="Réservoir plein" valuePropName="checked">
                <Select>
                  <Option value={true}>Oui (plein complet)</Option>
                  <Option value={false}>Non (appoint)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Notes additionnelles..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Annuler</Button>
              <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
                {editingRecord ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminFuel;
